import { useState } from 'react';
import type React from 'react';
import type { CSSProperties } from 'react';

import { CloudUploadRounded } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import { useResourceContext, useTranslate } from 'react-admin';
import { useDropzone } from 'react-dropzone';

import type { DropEvent, FileRejection, DropzoneOptions } from 'react-dropzone';

export interface DropZoneProps extends DropzoneOptions {
  hasError: boolean;
  onFileSelected: (file: File | undefined) => void;
  onFileRejected: (rejected: boolean) => void;
}

export const Dropzone = ({
  accept,
  hasError = false,
  onFileSelected,
  onFileRejected,
  onDragEnter,
  onDragLeave,
}: DropZoneProps): React.ReactElement => {
  const theme = useTheme();
  const [isFileRejected, setIsFileRejected] = useState(false);
  const resourceName = useResourceContext();
  const translate = useTranslate();

  const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[], _event: DropEvent): void => {
    if (fileRejections.length > 0) {
      setIsFileRejected(true);
      onFileRejected(true);
    } else {
      onFileSelected(acceptedFiles[0]);
      setIsFileRejected(false);
      onFileRejected(false);
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject } = useDropzone({
    accept,
    onDrop,
    onDragEnter,
    onDragLeave,
  });

  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.5),
    borderWidth: 2,
    borderRadius: 1,
    borderColor: theme.palette.grey[400],
    borderStyle: 'dashed',
    cursor: 'pointer',
    height: theme.spacing(12),
  };

  const style: CSSProperties = {
    ...baseStyle,
    ...(isDragAccept ? { borderColor: theme.palette.success.main } : {}),
    ...(isDragReject || isFileRejected || hasError ? { borderColor: theme.palette.error.main } : {}),
    ...(isDragActive ? { borderColor: theme.palette.primary.main } : {}),
  };

  return (
    <Box height='100%' marginLeft='auto' marginRight='auto' display='flex' flexDirection='column'>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <Box
          height='100%'
          display='flex'
          flexDirection='column'
          justifyContent='space-between'
          alignItems='center'
          color={theme.palette.text.secondary}
        >
          {resourceName !== undefined && (
            <Typography textAlign='center' variant='body2' sx={{ whiteSpace: 'pre-line' }}>
              {translate(`resources.${resourceName}.fields.fileUploadHint`)}
            </Typography>
          )}
          <CloudUploadRounded />
        </Box>
      </div>
    </Box>
  );
};
